import React, { useEffect, useMemo, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { connect } from 'react-redux';
import { type RouteComponentProps, Switch, withRouter } from 'react-router';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../actions/account.actions';
import { appsActions } from '../actions/apps.actions';
import Loading from '../components/Loading';
import { getOrgById } from '../components/RMS/organisation.services';
import { isAcceptInviteFlowInProgress } from '../components/Teammates/teammateutils';
import { useIsWebView } from '../custom-hooks/useIsWebView';
import { usePageTracking } from '../custom-hooks/usePageTracking';
import { gdpr } from '../helpers/gdpr';
import { store } from '../helpers/store';
import { isMobileDevice } from '../helpers/utilities';
import { notifyError } from '../layouts/Notify/actions';
import { getAccount } from '../selectors/account.selector';
import { flagService } from '../services';
import { useTracking } from '../services/amplitude/useTracking';
import { sendDatadogRumData } from '../services/datadogRUM';
import {
  initializeFeatureFlags,
  setFeatureFlagContext,
} from '../services/featureFlags/featureFlagBootstrap';
import { updateIntercomCompany } from '../services/intercom.service';
import { useIntercom, useProductionIntercomAppId } from '../services/intercom/useIntercom';
import { initialise as initaliseDataDogLogging } from '../services/loggerService';
import { accountRoutesConst } from './account.routes';
import { externalSupportUrl } from './accountRoutePaths';
import {
  acceptAllInvitesAndRouteToFirstAcceptedBrand,
  createRoute,
  DEFAULT_MIN_LOADING_TIMEOUT,
  getAppId,
  getIsLoggingIn,
  isAppLessPrivateRoute,
  isOrgInPathName,
  isPublicRoute,
} from './utils';

// App-less routes do not carry an app id in the URL, so we use the existing
// flipdish-global lookup token to resolve an accessible app when needed.
const DEFAULT_APP_LOOKUP_ID = 'flipdish-global';
const getResolvedAppHomePath = (appId: string) => `/${appId}/home`;
const isSupportPath = (pathname: string) => pathname === accountRoutesConst.Support;
const isSupportIntent = ({
  pathname,
  search,
}: Pick<RouteComponentProps['location'], 'pathname' | 'search'>) => {
  const searchParams = new URLSearchParams(search);

  return isSupportPath(pathname) || searchParams.get('open') === 'support';
};

type Props = RouteComponentProps & MappedState & MappedDispatch;
const AuthRouter: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const isAcceptInviteInProgress = isAcceptInviteFlowInProgress();

  const {
    account,
    currentApp,
    registeredRoutes,
    location,
    history,
    permissions,
    setCurrentApp,
    dispatchNotifyError,
    intercomCompanySyncEnabled,
  } = props;

  const isWebView = useIsWebView();
  const { identifyUser } = useTracking(account);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [intercomInitialized, setIntercomInitialized] = useState(false);
  const { showNewMessage: showNewIntercomMessage, update: updateIntercom } = useIntercom();
  const isAppLessRoute = isAppLessPrivateRoute(location.pathname);
  const isNoOrganisationAccessRoute = location.pathname === accountRoutesConst.NoOrganisationAccess;
  const selectedAppPermissionsLoaded = permissions.length > 0;

  //#region initialize
  const startLoadingTime = useRef<number>();
  const pathBefore = useRef<string>();
  const pendingSupportMessage = useRef<string | null>(null);
  useEffect(() => {
    startLoadingTime.current = Date.now();

    (function setPathBefore() {
      const { pathname } = location;
      // now any route matches private /:appId/ :(
      if (
        pathname !== '/' &&
        !isAppLessPrivateRoute(pathname) &&
        !isOrgInPathName(pathname) &&
        !isPublicRoute(pathname)
      ) {
        pathBefore.current = pathname;
      }
    })();

    props.getAccountDetails().catch(() => {
      // not login
      setTimeout(
        () => {
          setInitialized(true);
        },
        DEFAULT_MIN_LOADING_TIMEOUT - (Date.now() - startLoadingTime.current!)
      );
    });
  }, []);

  useEffect(() => {
    if (account.authorized && account.AccountId) {
      (async () => {
        if (isNoOrganisationAccessRoute && currentApp.AppId) {
          history.replace(getResolvedAppHomePath(currentApp.AppId));
          setInitialized(true);
          setLoading(false);
          return;
        }

        if (currentApp.AppId) {
          if (selectedAppPermissionsLoaded || isAppLessRoute) {
            setInitialized(true);
          }
          return;
        }

        if (!currentApp.AppId) {
          if (isOrgInPathName(location.pathname)) {
            setInitialized(true);
            setLoading(false);
            return;
          }

          // This will return FD-global app for FD staff always
          // non fd but existing client will get one or more apps back based on their account cookie
          // new signup will get no apps back

          const app = await setCurrentApp(
            isNoOrganisationAccessRoute ? DEFAULT_APP_LOOKUP_ID : getAppId(pathBefore),
            { suppressNoAppFoundError: account.IsSelfServeUser || isNoOrganisationAccessRoute }
          );

          if (isNoOrganisationAccessRoute && app?.AppId) {
            history.replace(getResolvedAppHomePath(app.AppId));
            setInitialized(true);
            setLoading(false);
            return;
          }

          if (!app?.AppId) {
            if (account.IsSelfServeUser && getIsLoggingIn()) {
              history.replace(accountRoutesConst.NoOrganisationAccess);
              pathBefore.current = undefined;
              setInitialized(true);
              setLoading(false);
              return;
            }

            if (isAppLessPrivateRoute(location.pathname)) {
              pathBefore.current = undefined;
              setInitialized(true);
              setLoading(false);
              return;
            }

            if (account.IsSelfServeUser) {
              history.replace(accountRoutesConst.NoOrganisationAccess);
              pathBefore.current = undefined;
            } else {
              if (!isAcceptInviteInProgress && getIsLoggingIn()) {
                await acceptAllInvitesAndRouteToFirstAcceptedBrand({
                  email: account?.Email ?? '',
                  dispatchNotifyError,
                });
              } else {
                dispatchNotifyError();
              }
            }
            setInitialized(true);
            setLoading(false);
            return;
          }

          if (pathBefore.current) {
            const path = pathBefore.current;
            pathBefore.current = undefined;
            if (path !== location.pathname) {
              history.replace(path);
            }
          }
        }
      })();
    }
  }, [
    account,
    currentApp,
    history,
    isAppLessRoute,
    location.pathname,
    setCurrentApp,
    selectedAppPermissionsLoaded,
    dispatchNotifyError,
    isAcceptInviteInProgress,
    isNoOrganisationAccessRoute,
  ]);
  //#endregion

  //#region page events trackers
  usePageTracking();
  //#endregion

  //#region GDPR -init on first load if not on create_password page
  gdpr.useInitGdpr();
  //#endregion

  //#region Feature flags - init providers on first load independently of GDPR-controlled RUM
  useEffect(() => {
    initializeFeatureFlags({
      enableExposureLogging: true,
      enableRumFeatureFlagTracking: true,
    });
  }, []);
  //#endregion

  //#region Split IO -init on first load to retrieve feature flags from Split
  const isFlipdishStaff = permissions?.includes('FlipdishStaff');
  useEffect(() => {
    const isLoggedIn = !!account?.Email;
    const TRAFFIC_TYPE = flagService.TRAFFIC_TYPE;
    const splitParams = {
      appId: currentApp.AppId,
      orgId: currentApp.OrgId,
      isFlipdishStaff,
      userId: account.AccountId || account.DeviceId,
      languageId: account.Language,
      trafficType: isLoggedIn
        ? TRAFFIC_TYPE.USER_TRAFFIC_TYPE
        : TRAFFIC_TYPE.ANONYMOUS_USER_TRAFFIC_TYPE,
    };

    flagService.initialiseSplit(store, splitParams);
    sendDatadogRumData(account, currentApp);
    setFeatureFlagContext(account, currentApp, isFlipdishStaff);
    identifyUser();
  }, [account?.AccountId, currentApp?.AppId, currentApp?.OrgId, isFlipdishStaff]);
  //#endregion

  //#region DataDog -init on first load to allow logging of errors
  useEffect(() => {
    initaliseDataDogLogging();
  }, []);
  //#endregion

  useEffect(() => {
    if (loading && isAcceptInviteInProgress && account.authorized) {
      setLoading(false);
    }
  }, [loading, isAcceptInviteInProgress, account.authorized]);

  //#region Intercom
  const allowIntercomForFlipdishStaff = useProductionIntercomAppId ? !isFlipdishStaff : true;
  // Initialize Intercom after permissions are loaded, but only for non-staff users in production
  useEffect(() => {
    const permissionsLoaded = permissions && permissions.length > 0;
    if (
      permissionsLoaded &&
      allowIntercomForFlipdishStaff &&
      intercomCompanySyncEnabled &&
      !intercomInitialized &&
      !isWebView &&
      !isAppLessRoute
    ) {
      (async () => {
        await gdpr.initGdprIntercomAfterLoad();
        setIntercomInitialized(true);
      })();
    }
  }, [
    permissions,
    allowIntercomForFlipdishStaff,
    intercomCompanySyncEnabled,
    intercomInitialized,
    isWebView,
    isAppLessRoute,
  ]);

  useEffect(() => {
    if (intercomInitialized) {
      updateIntercom({ hide_default_launcher: isWebView || isAppLessRoute });
    }
  }, [isWebView, isAppLessRoute, intercomInitialized]);

  useEffect(() => {
    if (isSupportIntent(location)) {
      pendingSupportMessage.current = new URLSearchParams(location.search).get('message') ?? '';
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (pendingSupportMessage.current === null) {
      return;
    }

    if (isMobileDevice() && !isFlipdishStaff) {
      pendingSupportMessage.current = null;
      window.location.replace(externalSupportUrl);
      return;
    }

    if (isSupportPath(location.pathname) && !currentApp.AppId) {
      return;
    }

    const supportMessage = pendingSupportMessage.current;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('open');
    searchParams.delete('message');

    const nextLocation = {
      ...location,
      pathname: isSupportPath(location.pathname)
        ? getResolvedAppHomePath(currentApp.AppId!)
        : location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    };

    if (isFlipdishStaff) {
      pendingSupportMessage.current = null;
      history.replace(nextLocation);
      return;
    }

    if (!intercomInitialized) {
      return;
    }

    showNewIntercomMessage(supportMessage);
    pendingSupportMessage.current = null;
    history.replace(nextLocation);
  }, [
    currentApp.AppId,
    history,
    intercomInitialized,
    isFlipdishStaff,
    location.pathname,
    location.search,
    showNewIntercomMessage,
  ]);

  // Update Intercom with current organization when they change
  useEffect(() => {
    const { OrgId: orgId } = currentApp ?? {};
    if (
      orgId &&
      intercomCompanySyncEnabled &&
      allowIntercomForFlipdishStaff &&
      intercomInitialized
    ) {
      (async () => {
        try {
          const orgInformation = await getOrgById(orgId);
          if (orgInformation.data.data) {
            await updateIntercomCompany(orgId, {
              orgName: orgInformation.data.data.name,
            });
          }

          updateIntercom({
            'Portal language': account.Language,
            created_at: account.CreatedAt
              ? Math.floor(new Date(account.CreatedAt).getTime() / 1000)
              : undefined,
          });
        } catch (error) {
          console.error('Error updating Intercom company', error);
        }
      })();
    }
  }, [
    currentApp?.OrgId,
    intercomCompanySyncEnabled,
    allowIntercomForFlipdishStaff,
    intercomInitialized,
  ]);
  //#endregion

  const routes = useMemo(() => {
    if (loading && !isAcceptInviteInProgress) {
      return null;
    }

    if (!account.authorized) {
      datadogRum.setUserProperty('setup_status', 'not_authenticated');
      datadogRum.setGlobalContextProperty('setup_status', 'not_authenticated');
      return registeredRoutes
        .filter((r) => r.group === 'public' || r.group === 'publicAndPrivate')
        .map((r) => createRoute(r, permissions));
    }

    if (isOrgInPathName(location.pathname)) {
      return null;
    }

    datadogRum.setUserProperty('setup_status', 'logged_in');
    datadogRum.setGlobalContextProperty('setup_status', 'logged_in');

    return registeredRoutes
      .filter((r) => r.group === 'private' || r.group === 'publicAndPrivate')
      .map((r) => createRoute(r, permissions));
  }, [loading, account, registeredRoutes, permissions, location.pathname]);

  if (loading) {
    return (
      <Loading
        fullscreen
        animate={!initialized}
        onAnimationEnd={() => {
          // When setInitialized(true) onAnimationEnd sets loading false
          setLoading(false);
        }}
      />
    );
  }

  return <Switch>{routes}</Switch>;
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    account: getAccount(state),
    currentApp: state.currentApp,
    permissions: state.permissions,
    registeredRoutes: state.routing.routes,
    intercomCompanySyncEnabled: flagService.isFlagOn(state, 'enableIntercomCompanySync'),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  getAccountDetails: () => dispatch(accountActions.getAccountDetails(true)),
  setCurrentApp: (
    appId: string | undefined,
    options?: Parameters<typeof appsActions.setCurrentApp>[1]
  ) => dispatch(appsActions.setCurrentApp(appId, options)),
  dispatchNotifyError: () =>
    dispatch(notifyError({ message: 'Something_went_wrong', translate: true })),
});

export default compose<Props, Record<string, never>>(
  setDisplayName('AuthRouter'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(AuthRouter);
